export { ORDERS_SCOPE } from "./navigation.ts";

import type { StateClassKey } from "@tdelivr/design-tokens";

import { type BoardRow, type DetailField } from "./board.ts";

/**
 * What the incoming orders board does to what COM-06 returns, away from any rendering.
 *
 * Built to `22 WP21 Merchant Portal.dc.html`, page `orders`:
 *
 * ```
 * orders:['Incoming orders','COM-06 · COM-07',
 *   'Accept or reject each order before the timer runs out, then move it through preparation.',
 *   [['New','3','human','accept within 3 min'],['Preparing','5','active',''],
 *    ['Ready for pickup','2','done','courier on the way'],['Rejected today','1','closed','']],
 *   ['Order','Items','Timer'],
 *   [['co_7Q31','3 items · KES 1,240.00','2:41 left to accept','human','new'],
 *    ['co_7Q2K','3 items · KES 559.00','ready by 12:55','active','preparing'],
 *    ['co_7Q28','6 items · KES 2,880.00','courier 3 min away','done','ready'],
 *    ['co_7Q20','1 item · KES 320.00','auto-rejected · timer','closed','rejected']],
 *   'info','An order not accepted in time is rejected automatically and the customer is refunded in full.']
 * ```
 *
 * ## What is drawn from data and what is not
 *
 * | drawn                                | source                                                   |
 * | ------------------------------------ | -------------------------------------------------------- |
 * | the rows                             | COM-06 `GET /v1/merchants/{merchantId}/orders`           |
 * | `Order` — `co_7Q31`                  | `orderId`, a UUID rather than the design's short code    |
 * | `Items` — `3 items`                  | `itemUnits`                                              |
 * | `Items` — `KES 1,240.00`             | **nothing** — the order total is COM-05's                |
 * | `Timer` — `2:41 left to accept`      | computed from `acceptanceDeadline`, which **is** published |
 * | `preparing`, `ready`                 | **nothing** — COM-07's states, and its reads are by id   |
 * | the four tiles                       | no listing publishes a total (R-04); no tile row here    |
 * | the primary command                  | drawn and refused — `PAUSE_REFUSAL`                      |
 *
 * ## The timer is the one countdown on this platform with a published source
 *
 * Every other board that draws one has had to record the absence — the support console's case queue
 * most recently, where nothing publishes a reply deadline at all. Here `acceptanceDeadline` is a
 * real field on a real row, written once by the order-placed ingest, and the design's own
 * `2:41 left to accept` is that instant minus now. So this board computes it, and says so.
 *
 * **It is computed against the browser's clock, which is the one caveat.** A merchant whose device
 * is a minute fast sees a minute less. The alternative is to draw the absolute instant and let them
 * subtract, which is worse on a board whose whole purpose is a countdown — and the service refuses
 * a late acceptance regardless of what this screen said, so the clock cannot cause a wrong decision
 * to be accepted. Recorded rather than hidden: the absolute deadline is on every row's title.
 */

/** COM-06's published state enum, as one name. */
export type AcceptanceState = "pending" | "accepted" | "rejected" | "expired";

/** One acceptance, as COM-06's listing publishes it. */
export interface Acceptance {
  readonly tenantId: string;
  readonly orderId: string;
  readonly orderVersion: number;
  readonly merchantId: string;
  readonly locationId: string;
  readonly itemUnits: number;
  readonly acceptanceDeadline: string;
  readonly state: AcceptanceState;
  readonly version: number;
  readonly placedAt: string;
  readonly updatedAt: string;
  readonly decidedBy?: string | undefined;
  readonly rejectionReason?: string | undefined;
}

/**
 * The state class and the word, from `state`.
 *
 * | state      | class    | word       | where the word comes from                        |
 * | ---------- | -------- | ---------- | ------------------------------------------------ |
 * | `pending`  | `human`  | `new`      | the design's `human · new` row                   |
 * | `accepted` | `active` | `accepted` | the contract's word; the design says `preparing` |
 * | `rejected` | `closed` | `rejected` | the design's `closed · rejected` row             |
 * | `expired`  | `failed` | `timed out`| the design's `auto-rejected · timer` cell        |
 *
 * **`pending` is `human` and that is the design's own choice, not a softening.** Its violet class
 * is for a row waiting on a person, and an order nobody has accepted is waiting on this merchant —
 * which is the entire point of the board. It is also the only board on this platform where the
 * `human` row is the normal case rather than the exception.
 *
 * **`accepted` takes the contract's word and not the design's.** The design writes `preparing`,
 * which is COM-07's state and a claim this board cannot make: COM-06 knows the order was accepted
 * and knows nothing about whether anybody has started on it. Saying `preparing` would tell a
 * merchant their kitchen had begun. R-35.
 *
 * **`expired` is `failed` and reads `timed out`.** The design's cell is `auto-rejected · timer`,
 * and the distinction worth keeping is that nobody rejected it: the deadline passed. `failed` is
 * the design's class for something that went wrong, and an order lost to a timer did.
 */
export function stateOf(acceptance: Acceptance): { classKey: StateClassKey; label: string } {
  switch (acceptance.state) {
    case "pending":
      return { classKey: "human", label: "new" };
    case "accepted":
      return { classKey: "active", label: "accepted" };
    case "rejected":
      return { classKey: "closed", label: "rejected" };
    case "expired":
      return { classKey: "failed", label: "timed out" };
  }
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(rows: readonly Acceptance[]): readonly string[] {
  return [...new Set(rows.map((row) => stateOf(row).label))];
}

/**
 * The design's `Items` cell: how many units, in its own grammar.
 *
 * `1 item` and `3 items`, which the design writes both of. The money the design puts after it has
 * no source on this board — the order total is COM-05's and this page has no route to it — so the
 * cell stops at the count and the row's title says why.
 */
export function itemsCell(acceptance: Acceptance): string {
  return `${String(acceptance.itemUnits)} ${acceptance.itemUnits === 1 ? "item" : "items"}`;
}

/**
 * The design's `Timer` cell: how long is left, or what happened instead.
 *
 * Four shapes, one per state, because the design draws four:
 *
 *   - pending and in time — `2:41 left to accept`, the design's own words
 *   - pending and past its deadline — `overdue`, because the service will expire it on its next
 *     sweep and telling a merchant they have `-0:12` left is telling them to hurry at something
 *     that has already gone
 *   - accepted — `accepted`, where the design writes `ready by 12:55`; nothing publishes a ready
 *     time, and COM-07 is by-identifier only
 *   - rejected or expired — the state's own word, since there is no longer a deadline to count to
 *
 * `now` is injected so a test can place the clock rather than wait for it.
 */
export function timerCell(acceptance: Acceptance, now: number): string {
  if (acceptance.state === "accepted") return "accepted";
  if (acceptance.state === "rejected") return "rejected";
  if (acceptance.state === "expired") return "timed out";

  const deadline = Date.parse(acceptance.acceptanceDeadline);
  if (Number.isNaN(deadline)) return acceptance.acceptanceDeadline;
  const remaining = Math.floor((deadline - now) / 1000);
  if (remaining <= 0) return "overdue";
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, "0")} left to accept`;
}

/**
 * One row, with its detail panel.
 *
 * The panel draws the absolute deadline, which the grid draws as a countdown — so a merchant
 * reading a row can check what this console computed against the time their own device says.
 */
export function orderRow(acceptance: Acceptance, now: number): BoardRow {
  const state = stateOf(acceptance);

  return {
    id: acceptance.orderId,
    cells: [
      {
        text: acceptance.orderId,
        // Mono, which the design's own grid does for a value matching its prefix list. The design
        // writes `co_7Q31`, a short code nothing publishes; this is the identifier COM-06 holds.
        mono: true,
        title:
          "The order identifier COM-06 holds. The design writes a short code — co_7Q31 — and nothing on any contract publishes one.",
      },
      {
        text: itemsCell(acceptance),
        title:
          /*
           * No figure in this sentence, deliberately. It used to quote the design's own sample total, and a
           * tooltip carrying an amount beside a cell that is not that amount is an amount a
           * merchant can read as this order's. The absence is named without a number.
           */
          "How many units are in the order. The design writes the order total beside it and COM-06 does not hold one: the total is COM-05's, which this board has no route to and would be a second service on one row. See R-35.",
      },
      {
        text: timerCell(acceptance, now),
        title: `The acceptance deadline is ${acceptance.acceptanceDeadline}. The countdown is computed against this device's clock, so a device that is fast shows less time — the service refuses a late acceptance whatever this screen said.`,
      },
    ],
    state: {
      ...state,
      title: `COM-06 publishes state ${acceptance.state}. ${describeState(acceptance)}`,
    },
    // The design tints its `human` and `failed` rows, and this board reaches both — which no other
    // board on this platform does. `pending` is the one waiting on this merchant.
    tinted: state.classKey === "human" || state.classKey === "failed",
    detail: detailFor(acceptance),
    history: historyFor(acceptance),
    // The design's own form glyph for this page.
    glyph: "receipt_long",
  };
}

function describeState(acceptance: Acceptance): string {
  switch (acceptance.state) {
    case "pending":
      return "Nobody has accepted or rejected it. An order not accepted in time is rejected automatically and the customer is refunded in full.";
    case "accepted":
      return "It was accepted. The design draws `preparing` here, which is COM-07's state — COM-06 does not know whether anybody has started on it.";
    case "rejected":
      return `It was rejected${acceptance.decidedBy === undefined ? "" : ` by ${acceptance.decidedBy}`}.`;
    case "expired":
      return "The deadline passed and the service expired it. Nobody rejected it, which is the distinction the design's `auto-rejected · timer` is making.";
  }
}

function detailFor(acceptance: Acceptance): readonly DetailField[] {
  return [
    { label: "Location", value: acceptance.locationId, mono: true },
    { label: "Units", value: String(acceptance.itemUnits) },
    { label: "Accept by", value: acceptance.acceptanceDeadline, mono: true },
    { label: "Order total", value: "—", absent: true },
    ...(acceptance.decidedBy === undefined
      ? []
      : [{ label: "Decided by", value: acceptance.decidedBy }]),
    ...(acceptance.rejectionReason === undefined
      ? []
      : [{ label: "Reason", value: acceptance.rejectionReason }]),
  ];
}

function historyFor(acceptance: Acceptance): readonly DetailField[] {
  return [
    { label: "Placed", value: acceptance.placedAt, mono: true },
    { label: "Last changed", value: acceptance.updatedAt, mono: true },
    { label: "Version", value: `v${String(acceptance.version)}`, mono: true },
  ];
}

/**
 * Why the designed primary action cannot be sent.
 *
 * The design's `Pause new orders` closes the shop: "Customers see you as closed until the pause
 * ends", with a branch, a duration and a reason. **Nothing on the platform models a pause.** COM-06
 * accepts and rejects one order at a time; MER-01 and MER-02 hold merchants and locations and
 * publish no open-or-closed state; COM-01's catalogue has a `published`/`archived` status, which is
 * whether a menu exists rather than whether a shop is taking orders tonight.
 *
 * The nearest thing available is archiving the catalogue, and it is not a substitute: it would hide
 * the menu permanently rather than pause ordering for an hour, and a merchant pressing `Pause` and
 * getting that would have to rebuild their menu. That is the kind of substitution that only looks
 * helpful until somebody uses it.
 */
export const PAUSE_REFUSAL =
  "Not available. Nothing on the platform models pausing orders: COM-06 decides one order at a time, and MER-01 and MER-02 publish no open-or-closed state for a merchant or a branch. Archiving the catalogue is not a substitute — it hides the menu permanently rather than pausing for an hour.";

/**
 * What this board owes a merchant beyond its rows.
 *
 * Two sentences. The first is the window, which every board on this platform states. The second is
 * this board's own: the countdown is the thing a merchant acts on, and it is computed here rather
 * than published, so they should know which clock it is against.
 */
export function listingNote(rows: readonly Acceptance[] | undefined): string | undefined {
  if (rows === undefined) return undefined;
  const waiting = rows.filter((row) => row.state === "pending").length;
  return (
    `Ordered by acceptance deadline, soonest first, which is COM-06's own order and the one column ` +
    `no decision rewrites. The countdown on each row is computed against this device's clock — the ` +
    `absolute deadline is on the row — and the service refuses a late acceptance whatever this ` +
    `screen showed. ` +
    (waiting === 0
      ? `Nothing is waiting on a decision.`
      : `${String(waiting)} order(s) are waiting on a decision.`)
  );
}
